import type { CityConfig } from "../city-template/types";

export const oceansideConfig: CityConfig = {
  city: "Oceanside",
  state: "California",
  stateAbbr: "CA",
  slug: "oceanside",
  route: "/cities/oceanside",
  title: "Emergency Passport Oceanside, CA | Fast Renewal & Expedited Services",
  metaDescription: "Emergency passport and expedited passport services in Oceanside, CA — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for North San Diego County travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Oceanside, CA",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport, fast passport renewal, or urgent passport help? We help Oceanside and North San Diego County travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Oceanside%20California%20coastal%20scene%20at%20golden%20hour%20with%20the%20iconic%20Oceanside%20Pier%20extending%20into%20the%20Pacific%20Ocean%2C%20sandy%20beach%20and%20palm%20trees%20along%20the%20shoreline%2C%20warm%20sunset%20light%20with%20orange%20and%20amber%20tones%2C%20surfers%20and%20coastal%20atmosphere%2C%20clean%20and%20modern%20seaside%20scene%2C%20professional%20landscape%20photography%2C%20wide%20panoramic%20view%2C%20no%20visible%20text%2C%20serene%20and%20beautiful%20coastal%20environment&width=1800&height=700&seq=oceanside-hero-001&orientation=landscape",
  heroImageAlt: "Oceanside California coastal scene at golden hour with the iconic pier extending into the Pacific Ocean",
  geoPosition: "33.1959;-117.3795",
  geoPlacename: "Oceanside, California",
  regionalAgency: {
    name: "Los Angeles Passport Agency",
    address: "300 N Los Angeles St, Los Angeles, CA 90012",
    distance: "Located in downtown LA — approximately 90 minutes from Oceanside via I-5",
  },
  nearbyCitiesZips: [
    { name: "Oceanside", zips: "92054, 92056, 92057, 92058" },
    { name: "Carlsbad", zips: "92008, 92009, 92010, 92011" },
    { name: "Vista", zips: "92081, 92083, 92084" },
    { name: "San Marcos", zips: "92069, 92078, 92079" },
    { name: "Escondido", zips: "92025, 92026, 92027, 92029" },
    { name: "Encinitas", zips: "92024, 92007" },
    { name: "Del Mar", zips: "92014" },
    { name: "Fallbrook", zips: "92028" },
    { name: "Camp Pendleton", zips: "92055" },
    { name: "San Diego", zips: "92101–92117" },
    { name: "Temecula", zips: "92590, 92591, 92592" },
    { name: "Murrieta", zips: "92563, 92564" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent North San Diego County departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "Fast passport renewal in San Diego", route: "/cities/san-diego" },
    { name: "Expedited passport services near Carlsbad", route: "/cities/carlsbad" },
    { name: "Emergency passport help near Escondido", route: "/cities/escondido" },
    { name: "Urgent passport services in Chula Vista", route: "/cities/chula-vista" },
    { name: "Rush passport help in Santa Monica", route: "/cities/santa-monica" },
    { name: "Fast passport renewal in Long Beach", route: "/cities/long-beach" },
    { name: "Expedited passport services in Los Angeles", route: "/cities/los-angeles" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Oceanside remotely via secure courier. We do not have a walk-in office in Oceanside — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for North San Diego County travelers.",
  fpcOfficeDistance: "Serving all of North San Diego County",
  serviceAreaIntro: "Fast Passport Center provides emergency passport, expedited passport, and fast passport renewal services throughout the entire Oceanside community. From the Oceanside Pier to Rancho Del Oro, from the San Luis Rey area to the coastal communities — we help North San Diego County travelers get their passports fast, often in 24–48 hours through our registered courier service.",
  nearbyCitiesIntro: "We serve all Oceanside neighborhoods and nearby North San Diego County cities including Carlsbad, Vista, San Marcos, Encinitas, Escondido, and Fallbrook. If you are in North San Diego County or near Camp Pendleton, we have you covered.",
  howItWorksIntro: "Most Oceanside travelers complete everything online and ship documents overnight. No need to drive to Los Angeles or wait in line at the regional agency.",
  mistakesIntro: "With San Diego International Airport (SAN) nearby and North County residents traveling frequently to Mexico and beyond, Oceanside travelers can't afford passport delays. Here are the most common mistakes we see.",
  testimonialsIntro: "Oceanside and North San Diego County travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about emergency passports, expedited passports, or fast passport renewal in Oceanside? Here are the most common ones we hear from North San Diego County travelers.",
  statePageRoute: "/states/california",
  statePageName: "California",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Bereavement or medical crisis", "Expect to wait all day or return next day"],
      highlight: false,
      emergency: true,
    },
    {
      speed: "3–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Ideal for upcoming trips", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–3",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns and budget fights", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For Oceanside travelers with immediate departure needs — as fast as 24 hours for urgent international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast passport renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-alert-line",
      title: "Lost Passport Replacement",
      description: "Emergency passport replacement for lost or stolen passports with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement in 24–48 hours"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Expedited passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-time-line",
      title: "Same-Day Passport Help",
      description: "Urgent same-day passport assistance for Oceanside travelers with flights within 24–72 hours.",
      bullets: ["Same-day document review", "Prepaid overnight shipping", "Direct courier hand-delivery"],
      badge: "Fastest",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-truck-line",
      title: "Passport Courier Service",
      description: "Registered courier service that hand-delivers your passport documents directly to State Department processing facilities.",
      bullets: ["Registered State Department courier", "Bypass standard mail delays", "Trackable delivery both ways"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent emergency passport processing for Oceanside travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Expedited Passport", description: "Fast expedited passport processing for Oceanside travelers with urgent international travel." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "Lost Passport Replacement", description: "Emergency passport replacement service for lost or stolen passports in Oceanside, including DS-64 form guidance." },
    { name: "Child Passport", description: "Expedited passport services for minors under 16 in Oceanside, with parental consent guidance." },
    { name: "Passport Courier Service", description: "Registered passport courier service for Oceanside that hand-delivers documents directly to State Department facilities." },
  ],
  testimonials: [
    {
      name: "Sgt. James T.",
      location: "Camp Pendleton, CA",
      rating: 5,
      text: "Got emergency leave orders for overseas and my passport was expired. Fast Passport Center got me squared away in 48 hours. They understand military urgency.",
      service: "Emergency Passport",
    },
    {
      name: "Lisa M.",
      location: "Oceanside, CA",
      rating: 5,
      text: "Needed to renew for a family vacation to Hawaii. The process was smooth, they reviewed my documents, and I had my passport in 4 days. Highly recommend.",
      service: "Passport Renewal",
    },
    {
      name: "Carlos R.",
      location: "Vista, CA",
      rating: 5,
      text: "Lost my passport before a business trip to Mexico City. They walked me through the DS-64 process and got me a replacement in 36 hours. Incredible service.",
      service: "Lost Passport",
    },
    {
      name: "Jennifer K.",
      location: "Carlsbad, CA",
      rating: 5,
      text: "As a frequent international traveler from North County, I can't afford passport delays. Fast Passport Center has been my go-to for years. Professional and reliable.",
      service: "Expedited Renewal",
    },
  ],
  facilities: [
    {
      id: "oceanside-1",
      name: "Oceanside Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "221 N El Camino Real",
      city: "Oceanside",
      state: "CA",
      zip: "92054",
      phone: "(760) 433-8410",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.5 miles from Oceanside Pier",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Acceptance hours are limited. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "oceanside-2",
      name: "Carlsbad Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "2775 El Camino Real",
      city: "Carlsbad",
      state: "CA",
      zip: "92008",
      phone: "(760) 434-7442",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "1.0 miles from Carlsbad Village",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Busy location — book appointments 2+ weeks in advance. Limited weekend hours.",
      icon: "ri-mail-line",
    },
    {
      id: "oceanside-3",
      name: "Vista Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1850 Thibodo Rd",
      city: "Vista",
      state: "CA",
      zip: "92081",
      phone: "(760) 726-7350",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.8 miles from downtown Vista",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Popular for inland North County residents. Appointment strongly recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "oceanside-4",
      name: "San Marcos Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "768 E Barham Dr",
      city: "San Marcos",
      state: "CA",
      zip: "92069",
      phone: "(760) 744-7110",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "1.2 miles from California State University San Marcos",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for inland North County residents. Photo services available. Book ahead.",
      icon: "ri-mail-line",
    },
    {
      id: "oceanside-5",
      name: "Escondido Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "150 E Valley Pkwy",
      city: "Escondido",
      state: "CA",
      zip: "92025",
      phone: "(760) 745-8111",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.5 miles from downtown Escondido",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves inland North San Diego County. Limited passport hours — call to confirm.",
      icon: "ri-mail-line",
    },
    {
      id: "oceanside-6",
      name: "Camp Pendleton Pass & ID",
      type: "Military Installation Passport Acceptance",
      address: "Camp Pendleton, Building 2025",
      city: "Oceanside",
      state: "CA",
      zip: "92055",
      phone: "(760) 725-5114",
      hours: "Mon–Fri: 8:00 AM – 4:00 PM",
      distance: "On base — active duty and dependents only",
      website: "https://www.mccsmb.com/passports",
      websiteLabel: "Camp Pendleton MCCS",
      notes: "Available to active duty military, dependents, and retirees with base access. Passport photos available on-site.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — downtown Los Angeles", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "North County photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at San Diego area acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many North San Diego County applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the LA regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many North County families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "North San Diego travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Oceanside." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Oceanside address — trackable every step of the way." },
  ],
  faqs: [
    {
      q: "Can I get an emergency passport in Oceanside before international travel?",
      a: "Yes. Fast Passport Center provides emergency passport services in Oceanside for travelers with urgent international flights. Our registered couriers hand-deliver your documents directly to U.S. Department of State facilities, achieving 24–48 hour turnaround for most North San Diego County travelers.",
    },
    {
      q: "How fast can I renew my passport in Oceanside?",
      a: "Fast Passport Center offers fast passport renewal in Oceanside in as little as 24–48 hours through our emergency courier service. For less urgent travel, our 3–5 day expedited option provides the best value. Both options include full document review and a dedicated agent.",
    },
    {
      q: "What if my passport expires before my cruise?",
      a: "If your passport expires before your cruise, contact Fast Passport Center immediately. Many cruise lines require your passport to be valid for at least 6 months beyond your travel date. We can expedite your passport renewal in Oceanside in 24–48 hours so you don't miss your departure.",
    },
    {
      q: "Can I get a same-day passport appointment near Oceanside?",
      a: "Same-day passport appointments are only available at the Los Angeles Regional Passport Agency for documented life-or-death emergencies with proof of travel within 72 hours. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option available to Oceanside residents.",
    },
    {
      q: "Can I expedite a child passport in Oceanside?",
      a: "Yes. Fast Passport Center expedites child passports in Oceanside for minors under 16. Both parents must appear or submit notarized consent, along with the child's birth certificate and both parents' IDs. We walk North San Diego County families through the entire process.",
    },
    {
      q: "What should I do if I lost my passport before a trip?",
      a: "If you lost your passport before a trip, contact Fast Passport Center immediately. We provide lost passport replacement services in Oceanside with DS-64 form guidance and identity verification support. Our emergency courier service can get your replacement passport in 24–48 hours.",
    },
    {
      q: "How fast can I get a passport in Oceanside?",
      a: "With Fast Passport Center's emergency courier service, Oceanside travelers can receive their passport in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities, bypassing standard mail delays.",
    },
    {
      q: "Do I need to drive to the Los Angeles Passport Agency?",
      a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change.",
    },
    {
      q: "Where are the closest passport acceptance facilities in Oceanside?",
      a: "Popular options include the Oceanside Main Post Office (92054), Carlsbad Post Office (92008), Vista Post Office (92081), and San Marcos Post Office (92069). For military personnel, Camp Pendleton Pass & ID also offers services. All require appointments. We help you find the most convenient location and schedule your visit.",
    },
    {
      q: "Can I get a same-day passport in Oceanside?",
      a: "Same-day passports are only available at the Los Angeles Regional Passport Agency for documented life-or-death emergencies with proof of travel within 72 hours. For all other urgent travel, Fast Passport Center's 24–48 hour emergency processing is the fastest reliable option.",
    },
    {
      q: "What if I'm stationed at Camp Pendleton?",
      a: "We serve military personnel and families stationed at Camp Pendleton. Active duty can also use the on-base Pass & ID office for acceptance services. Our overnight shipping labels work from any UPS location, including near base entrances.",
    },
    {
      q: "How much does expedited passport service cost in Oceanside?",
      a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24–48 hour processing, 3–5 day expedited, or standard options. Contact us for a personalized quote based on your timeline.",
    },
    {
      q: "Do you help with passport photos in Oceanside?",
      a: "We guide you on exact photo requirements and can recommend locations near you. Many CVS, Walgreens, and FedEx locations in Oceanside, Carlsbad, and Vista offer passport photo services. We review your photos before submission to ensure they meet State Department standards.",
    },
    {
      q: "What documents do I need for a first-time passport in Oceanside?",
      a: "You'll need proof of citizenship (birth certificate or naturalization certificate), government-issued photo ID (driver's license or REAL ID), two passport photos, and payment. We provide a complete checklist and review your documents before you visit the acceptance clerk.",
    },
    {
      q: "Can you help with a child's passport in Oceanside?",
      a: "Yes. Child passports require both parents (or notarized consent from the absent parent), the child's birth certificate, and both parents' IDs. We walk North San Diego County families through the entire process and help schedule acceptance clerk appointments.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Oceanside", address: "1715 Oceanside Blvd, Oceanside, CA 92054", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Carlsbad", address: "2687 El Camino Real, Carlsbad, CA 92008", hours: "Mon–Sun: 7AM–11PM" },
    { name: "FedEx Office — Oceanside", address: "2455 Vista Way, Oceanside, CA 92054", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
    { name: "UPS Store — Vista", address: "1308 E Vista Way, Vista, CA 92084", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
    { name: "Walmart — San Marcos", address: "732 Center Dr, San Marcos, CA 92069", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Rite Aid — Escondido", address: "340 E Valley Pkwy, Escondido, CA 92025", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Postal Annex — Encinitas", address: "262 N El Camino Real, Encinitas, CA 92024", hours: "Mon–Fri: 9AM–6PM, Sat: 9AM–4PM" },
    { name: "Costco — Carlsbad", address: "951 Palomar Airport Rd, Carlsbad, CA 92011", hours: "Mon–Fri: 10AM–8:30PM, Sat–Sun: 9:30AM–6PM" },
  ],
  dmvLocations: [
    {
      name: "DMV — Oceanside",
      address: "2760 Oceanside Blvd, Oceanside, CA 92054",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID and driver's license renewals. Appointment required.",
      featured: true,
    },
    {
      name: "DMV — Carlsbad",
      address: "2621 El Camino Real, Carlsbad, CA 92008",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Passport-ready photo services available. Online appointment booking.",
      featured: false,
    },
    {
      name: "DMV — Escondido",
      address: "1450 E Valley Pkwy, Escondido, CA 92027",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID processing. Walk-ins limited — book online first.",
      featured: false,
    },
    {
      name: "DMV — Vista",
      address: "1830 University Dr, Vista, CA 92083",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Full-service center for northern San Diego County residents.",
      featured: true,
    },
  ],
  airports: [
    {
      code: "SAN",
      name: "San Diego International Airport",
      distance: "40 miles from Oceanside",
      address: "3225 N Harbor Dr, San Diego, CA 92101",
      note: "The primary international airport for San Diego County. International flights to Mexico, Canada, Europe, and Asia.",
      flightUrl: "https://www.san.org/",
      flightLabel: "San Diego Airport",
      featured: true,
    },
    {
      code: "LAX",
      name: "Los Angeles International Airport",
      distance: "85 miles from Oceanside",
      address: "1 World Way, Los Angeles, CA 90045",
      note: "Major international hub for long-haul flights to Asia, Europe, and South America. Accessible via I-5.",
      flightUrl: "https://www.flylax.com/",
      flightLabel: "LAX Flight Info",
      featured: false,
    },
    {
      code: "CRQ",
      name: "McClellan-Palomar Airport",
      distance: "12 miles from Oceanside",
      address: "2198 Palomar Airport Rd, Carlsbad, CA 92011",
      note: "Regional airport with private and charter flights. Some commercial service available.",
      flightUrl: "https://www.carlsbadca.gov/",
      flightLabel: "Palomar Airport",
      featured: false,
    },
  ],
};